import { useMemo } from 'react'
import type { Address } from 'viem'
import { useAccount, useBalance, useReadContracts } from 'wagmi'
import { erc20Abi } from '../lib/abis'
import { NATIVE_TOKEN_ADDRESS, isNative, type TokenMeta } from '../lib/tokens'

export type BalanceMap = Record<Address, bigint>

/**
 * ERC20 balances for the whole picker in one Multicall3 round trip, plus native FLR.
 *
 * Native FLR is keyed under `NATIVE_TOKEN_ADDRESS` like any other entry, so the picker, the
 * "Balance" link and the over-balance check need no special case. It is read with
 * `eth_getBalance` and kept out of the multicall: the sentinel is not a contract, and a
 * `balanceOf` against it would fail and — correctly — be left out of the map.
 */
export function useBalances(tokens: TokenMeta[]): {
  balances: BalanceMap
  native: bigint | null
  refetch: () => void
} {
  const { address: owner } = useAccount()

  const native = useBalance({ address: owner, query: { enabled: Boolean(owner), refetchInterval: 20_000 } })

  const erc20s = useMemo(() => tokens.filter((token) => !isNative(token)), [tokens])

  const reads = useReadContracts({
    allowFailure: true,
    contracts: erc20s.map((token) => ({
      address: token.address,
      abi: erc20Abi,
      functionName: 'balanceOf' as const,
      args: [owner as Address] as const,
    })),
    query: { enabled: Boolean(owner) && erc20s.length > 0, refetchInterval: 20_000 },
  })

  const nativeValue = native.data ? native.data.value : undefined

  const balances = useMemo(() => {
    const map: BalanceMap = {}
    if (nativeValue !== undefined) map[NATIVE_TOKEN_ADDRESS] = nativeValue
    erc20s.forEach((token, index) => {
      const entry = reads.data?.[index]
      // A token that fails `balanceOf` is left out rather than shown as zero: zero is a
      // claim about the wallet, and this is a claim about the contract.
      if (entry && entry.status === 'success' && typeof entry.result === 'bigint') {
        map[token.address] = entry.result
      }
    })
    return map
  }, [erc20s, reads.data, nativeValue])

  return {
    balances,
    native: nativeValue ?? null,
    refetch: () => {
      void reads.refetch()
      void native.refetch()
    },
  }
}
